import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Bed, Bath, Car, Square, MapPin, Calendar, ExternalLink, Mail, Phone, MessageCircle, Building2, DollarSign, Ruler, LandPlot, Heart, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Property } from '../types/property';
import { AffordabilityCalculator } from './AffordabilityCalculator';
import { ShareButtons } from './ShareButtons';
import { AreaAlerts } from './AreaAlerts';
import { ContactAgentForm } from './ContactAgentForm';
import { ImageLightbox, LightboxTrigger } from './ImageLightbox';
import { getAgentById } from '../lib/agents-api';
import { recordPropertyView } from '../lib/viewing-time-api';
import { Agent } from '../types/agent';
import { getContactEmail } from '@/config/runtime-config';

interface PropertyDetailProps {
  property: Property;
  onBack: () => void;
  isAgentView?: boolean;
  isFavorite?: boolean;
  onToggleFavorite?: (propertyId: string) => void;
}

// Status color mapping per design spec v2.1
const STATUS_COLORS: Record<string, string> = {
  Active: 'bg-[#10B981] text-white',
  Sold: 'bg-[#EF4444] text-white',
  Pending: 'bg-[#F59E0B] text-black',
  Withdrawn: 'bg-[#6B7280] text-white',
  Rented: 'bg-blue-500 text-white',
  Cancelled: 'bg-[#6B7280] text-white',
  Expired: 'bg-gray-600 text-white',
};

// Display labels for property types (v2.1 spec)
const TYPE_LABELS: Record<string, string> = {
  House: 'House',
  Apartment: 'Apartment',
  Townhouse: 'Townhouse',
  Land: 'Vacant Land',
  Farm: 'Farm',
  VacantLand: 'Vacant Land',
  Commercial: 'Commercial',
};

// Default "Something Property" contact info
function getDefaultContact(): Agent {
  return {
    id: 'something-property',
    name: 'Something Property',
    email: getContactEmail(),
    phone: '+27 (011) 862-1790',
    whatsapp: '+27615483584',
    title: 'Property Consultants',
    bio: 'Your trusted property partner',
    status: 'active' as const,
    specializations: [],
    propertyTypes: [],
    yearsExperience: 0,
    languages: ['English'],
    achievements: [],
    totalListings: 0,
    soldProperties: 0,
    rating: 5.0,
    joinedDate: new Date()
  };
}

// v2.1 spec: format price consistently as R X XXX XXX
function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function PropertyDetail({ property, onBack, isAgentView = false, isFavorite, onToggleFavorite }: PropertyDetailProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showAreaAlerts, setShowAreaAlerts] = useState(false);
  const [agentInfo, setAgentInfo] = useState<Agent | null>(null);
  const [loadingAgent, setLoadingAgent] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Viewing time tracking
  const startTimeRef = useRef<number>(Date.now());
  const sessionIdRef = useRef<string>(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const hasRecordedRef = useRef<boolean>(false);

  const media = property.media || [];
  const hasMedia = media.length > 0;

  const statusKey = property.listingStatus || 'Active';

  // Fetch real agent data on mount
  useEffect(() => {
    async function loadAgent() {
      setLoadingAgent(true);
      try {
        // Collect all possible identifiers to search the internal directory
        const lookupIds: string[] = [];
        if (property.agent?.id) lookupIds.push(property.agent.id);
        if (property.agent?.email) lookupIds.push(property.agent.email);
        if (property.agentId) lookupIds.push(property.agentId);
        if (property.compositeAgentIds?.length) lookupIds.push(...property.compositeAgentIds);
        if (property.externalAgentId) lookupIds.push(property.externalAgentId);

        // Try each identifier against the internal agent directory
        let fullAgent: Agent | null = null;
        for (const lookupId of lookupIds) {
          if (!lookupId) continue;
          fullAgent = await getAgentById(lookupId);
          if (fullAgent) break;
        }

        if (fullAgent) {
          // Found a full internal profile — merge listing phone/whatsapp if Cognito has none
          const embeddedPhone = property.agent?.phone || '';
          setAgentInfo({
            ...fullAgent,
            phone: fullAgent.phone || embeddedPhone,
            whatsapp: fullAgent.whatsapp || embeddedPhone,
          });
          console.log(`[PropertyDetail] Agent matched in directory: ${fullAgent.name}`);
        } else if (property.agent && (property.agent.name || property.agent.email || property.agent.phone)) {
          // Property has embedded agent data from the listing but no internal profile
          // Build a presentable Agent from the embedded data
          const embedded = property.agent;
          const defaultContact = getDefaultContact();
          setAgentInfo({
            ...defaultContact,
            id: embedded.id || embedded.email || 'external-agent',
            name: embedded.name || 'Property Agent',
            email: embedded.email || defaultContact.email,
            phone: embedded.phone || defaultContact.phone,
            whatsapp: embedded.whatsapp || embedded.phone || defaultContact.whatsapp,
            title: 'Property Consultant',
          });
          console.log(`[PropertyDetail] Using embedded agent data: ${embedded.name}`);
        } else {
          // No agent data at all — show generic Something Property contact
          setAgentInfo(getDefaultContact());
          console.log('[PropertyDetail] No agent assigned — showing Something Property contact');
        }
      } catch (error) {
        logger.error('Error loading agent', error as Error, undefined, 'PropertyDetail');
        // Even on error, prefer embedded agent data over default
        if (property.agent?.name) {
          const defaultContact = getDefaultContact();
          setAgentInfo({
            ...defaultContact,
            id: property.agent.id || 'external-agent',
            name: property.agent.name,
            email: property.agent.email || defaultContact.email,
            phone: property.agent.phone || defaultContact.phone,
            whatsapp: property.agent.whatsapp || property.agent.phone || defaultContact.whatsapp,
            title: 'Property Consultant',
          });
        } else {
          setAgentInfo(getDefaultContact());
        }
      } finally {
        setLoadingAgent(false);
      }
    }
    
    loadAgent();
  }, [property.id, property.agentId, property.agent]);

  // Track viewing time when component unmounts
  useEffect(() => {
    startTimeRef.current = Date.now();
    hasRecordedRef.current = false;

    return () => {
      // Only record if user spent at least 3 seconds on the page
      const viewDuration = (Date.now() - startTimeRef.current) / 1000;
      
      if (!hasRecordedRef.current && viewDuration >= 3) {
        hasRecordedRef.current = true;
        
        recordPropertyView(
          property.id,
          viewDuration,
          isAgentView ? 'agent' : 'anonymous',
          sessionIdRef.current
        ).catch(error => {
          logger.error('Failed to record property view', error as Error, undefined, 'PropertyDetail');
        });
      }
    };
  }, [property.id, isAgentView]);

  const handlePreApproval = () => {
    const preApprovalUrl = `/preapproval/start?propertyId=${property.id}&price=${property.price}&suburb=${property.address.suburb}`;
    window.open(preApprovalUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            data-testid="property-detail-back-button"
            variant="outline"
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {isAgentView ? 'Back to Hub' : 'Back to Properties'}
          </Button>

          <div className="flex items-center gap-2">
            {/* Favorite button */}
            {onToggleFavorite && (
              <Button
                data-testid="property-detail-favorite-button"
                variant="outline"
                size="sm"
                onClick={() => onToggleFavorite(property.id)}
                className={isFavorite ? 'border-red-500/50 text-red-500' : ''}
              >
                <Heart className={`w-4 h-4 mr-1.5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                {isFavorite ? 'Saved' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden">
              <div className="relative aspect-[16/10]">
                <ImageWithFallback
                  src={hasMedia ? media[currentImageIndex]?.url : ''}
                  alt={hasMedia ? media[currentImageIndex]?.alt || property.title : property.title}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => hasMedia && setLightboxOpen(true)}
                />
                
                {/* Image Navigation Dots */}
                {hasMedia && media.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                    <div className="flex gap-2 bg-black/50 rounded-lg p-2">
                      {media.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Lightbox trigger */}
                {hasMedia && (
                  <LightboxTrigger onClick={() => setLightboxOpen(true)} />
                )}

                {/* Badges */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  <Badge className={`${STATUS_COLORS[statusKey] || STATUS_COLORS.Active} border-0 shadow-lg`}>
                    {statusKey}
                  </Badge>
                  {property.noTransferDuty && (
                    <Badge className="bg-emerald-600 text-white border-0 shadow-lg">No Transfer Duty</Badge>
                  )}
                  {property.priceReduced && (
                    <Badge className="bg-red-600 text-white border-0 shadow-lg">Price Reduced</Badge>
                  )}
                  {property.flags.new && (
                    <Badge className="bg-blue-600 text-white border-0 shadow-lg">New</Badge>
                  )}
                  {property.flags.featured && (
                    <Badge className="bg-primary text-primary-foreground border-0 shadow-lg">Featured</Badge>
                  )}
                </div>
              </div>

              {/* Thumbnail strip */}
              {hasMedia && media.length > 1 && (
                <div className="p-4 border-t">
                  <div className="flex gap-2 overflow-x-auto">
                    {media.map((m, index) => (
                      <button
                        key={m.id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-16 rounded border-2 overflow-hidden ${
                          index === currentImageIndex ? 'border-primary' : 'border-border'
                        }`}
                      >
                        <ImageWithFallback
                          src={m.url}
                          alt={m.alt}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Property Details */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    {/* Title — spec: 28px bold on detail page */}
                    <CardTitle className="text-2xl">{property.title}</CardTitle>
                    <div className="flex items-center gap-2 text-muted-foreground mt-1">
                      <MapPin className="w-4 h-4" />
                      <span>{[property.address.suburb, property.address.city].filter(Boolean).join(', ')}</span>
                    </div>
                    {/* Status & badge row */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge className={`${STATUS_COLORS[statusKey] || STATUS_COLORS.Active} border-0 text-xs`}>
                        {statusKey}
                      </Badge>
                      {property.mandateType && (
                        <Badge variant="outline">For {property.mandateType}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {/* Price — spec: 36px bold on detail hero */}
                    <div className="text-3xl font-bold text-primary">
                      {formatPrice(property.price)}
                    </div>
                    <Badge variant="outline">{TYPE_LABELS[property.type] || property.type}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Quick Info Bar — spec: icon + count for bedrooms, bathrooms, parking, erf, floor */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {property.facts.bedrooms != null && property.facts.bedrooms > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Bed className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-bold text-lg">{property.facts.bedrooms}</div>
                        <div className="text-xs text-muted-foreground">Bedrooms</div>
                      </div>
                    </div>
                  )}
                  {property.facts.bathrooms != null && property.facts.bathrooms > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Bath className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-bold text-lg">{property.facts.bathrooms}</div>
                        <div className="text-xs text-muted-foreground">Bathrooms</div>
                      </div>
                    </div>
                  )}
                  {property.facts.parking != null && property.facts.parking > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Car className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-bold text-lg">{property.facts.parking}</div>
                        <div className="text-xs text-muted-foreground">Parking</div>
                      </div>
                    </div>
                  )}
                  {property.facts.floorSize != null && property.facts.floorSize > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Ruler className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-bold text-lg">{property.facts.floorSize} m²</div>
                        <div className="text-xs text-muted-foreground">Floor Area</div>
                      </div>
                    </div>
                  )}
                  {property.facts.erfSize != null && property.facts.erfSize > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <LandPlot className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-bold text-lg">{property.facts.erfSize} m²</div>
                        <div className="text-xs text-muted-foreground">Erf Size</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* v2.1 Features Grid */}
                {property.features && property.features.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">Features</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {property.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2.5 rounded-lg border border-border/50 bg-muted/30">
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium">{feature.type}</span>
                            {feature.value != null && (
                              <span className="text-sm text-muted-foreground">: {feature.value}</span>
                            )}
                            {feature.description && (
                              <p className="text-xs text-muted-foreground truncate">{feature.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amenities */}
                {property.amenities.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">Amenities</h3>
                    <div className="flex flex-wrap gap-2">
                      {property.amenities.map((amenity) => (
                        <Badge key={amenity} variant="secondary">
                          {amenity}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Floor Plan — F-017 fix */}
                <div data-testid="floor-plan">
                  <h3 className="font-semibold mb-3">Floor Plan</h3>
                  {(property as any).floorPlanUrl ? (
                    <img
                      src={(property as any).floorPlanUrl}
                      alt="Floor plan"
                      data-testid="floor-plan-image"
                      className="w-full rounded-lg border border-border/50"
                    />
                  ) : (
                    <div
                      data-testid="floor-plan-placeholder"
                      className="flex items-center justify-center p-8 rounded-lg bg-muted/30 border border-dashed border-border/50 text-muted-foreground text-sm"
                    >
                      Floor plan not available
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold mb-3">Description</h3>
                  <div
                    className="text-muted-foreground leading-relaxed prose prose-sm prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: property.description }}
                  />
                </div>

                {/* Financial Details — spec: Price, Rates, Levy, Transfer Duty */}
                {(property.rates || property.levy || property.noTransferDuty) && (
                  <div>
                    <h3 className="font-semibold mb-3">Financial Details</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="text-xs text-muted-foreground mb-1">Price</div>
                        <div className="font-bold text-primary">{formatPrice(property.price)}</div>
                      </div>
                      {property.rates != null && property.rates > 0 && (
                        <div className="p-3 rounded-lg bg-muted/50">
                          <div className="text-xs text-muted-foreground mb-1">Rates (monthly)</div>
                          <div className="font-medium">{formatPrice(property.rates)}</div>
                        </div>
                      )}
                      {property.levy != null && property.levy > 0 && (
                        <div className="p-3 rounded-lg bg-muted/50">
                          <div className="text-xs text-muted-foreground mb-1">Levy (monthly)</div>
                          <div className="font-medium">{formatPrice(property.levy)}</div>
                        </div>
                      )}
                      {property.noTransferDuty && (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                          <div className="text-xs text-muted-foreground mb-1">Transfer Duty</div>
                          <div className="font-medium text-emerald-500">No Transfer Duty</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Listing Info — spec: "Listed on" date */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4 border-t flex-wrap">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>Listed {new Date(property.listingDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  {property.somethingPropertyId && (
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-xs opacity-60">{property.somethingPropertyId}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Agent Card — spec: profile photo, name, title, contact buttons */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Agent</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingAgent ? (
                  <div className="text-center py-4 text-muted-foreground">
                    Loading agent info...
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <Avatar className="w-16 h-16">
                        {agentInfo?.id === 'something-property' ? (
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            <Building2 className="w-8 h-8" />
                          </AvatarFallback>
                        ) : (
                          <>
                            <AvatarImage src={agentInfo?.avatar} alt={agentInfo?.name} />
                            <AvatarFallback>{agentInfo?.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                          </>
                        )}
                      </Avatar>
                      <div className="flex-1">
                        <div className="font-semibold text-lg">{agentInfo?.name}</div>
                        <div className="text-sm text-muted-foreground">{agentInfo?.title}</div>
                      </div>
                    </div>

                    {/* Contact Details */}
                    <div className="space-y-3 mb-4">
                      {/* Email */}
                      <a
                        href={`mailto:${agentInfo?.email}`}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors group"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <Mail className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">Email</div>
                          <div className="font-medium truncate">{agentInfo?.email}</div>
                        </div>
                      </a>

                      {/* Phone */}
                      <a
                        href={`tel:${agentInfo?.phone}`}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors group"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                          <Phone className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">Phone</div>
                          <div className="font-medium">{agentInfo?.phone}</div>
                        </div>
                      </a>

                      {/* WhatsApp */}
                      <a
                        href={`https://wa.me/${agentInfo?.whatsapp?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi ${agentInfo?.name}, I'm interested in the property: ${property.title} (${formatPrice(property.price)})`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors group bg-green-500/5"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                          <MessageCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">WhatsApp</div>
                          <div className="font-medium">{agentInfo?.whatsapp}</div>
                        </div>
                      </a>
                    </div>

                    {/* Share Property */}
                    <div className="pt-3 border-t">
                      <ShareButtons
                        property={property}
                        agentPhone={agentInfo?.phone}
                        agentEmail={agentInfo?.email}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Contact Agent Form — spec: Name, Email, Phone, Message with validation */}
            {!loadingAgent && agentInfo && (
              <ContactAgentForm
                propertyTitle={property.title}
                propertyPrice={formatPrice(property.price)}
                agentName={agentInfo.name}
                agentEmail={agentInfo.email}
                propertyId={property.id}
                agentId={property.agentId}
              />
            )}

            {/* Action Buttons */}
            <Card>
              <CardContent className="pt-6 space-y-3">
                <Button
                  data-testid="property-detail-preapproval-button"
                  onClick={handlePreApproval}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Get Pre-Approved
                </Button>

                <Button
                  data-testid="property-detail-calculator-button"
                  onClick={() => setShowCalculator(!showCalculator)}
                  variant="outline"
                  className="w-full"
                >
                  Calculate Affordability
                </Button>

                <Button
                  data-testid="property-detail-area-alerts-button"
                  onClick={() => setShowAreaAlerts(!showAreaAlerts)}
                  variant="outline"
                  className="w-full"
                >
                  Get Area Alerts
                </Button>
              </CardContent>
            </Card>

            {/* Affordability Calculator */}
            {showCalculator && (
              <AffordabilityCalculator propertyPrice={property.price} />
            )}

            {/* Area Alerts */}
            {showAreaAlerts && (
              <AreaAlerts
                defaultArea={property.address.suburb}
                defaultCity={property.address.city}
                onClose={() => setShowAreaAlerts(false)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxOpen && hasMedia && (
        <ImageLightbox
          images={media}
          initialIndex={currentImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}